import { useState, useEffect } from "react";
import { Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminCreateInsight, adminUpdateInsight, adminGenerateCover, resolveImg } from "@/lib/api";

const CATEGORIES = ["AI", "Marketing Strategy", "Website Design", "Social Media", "Content", "Automation", "Opinion"];

const EMPTY = { title: "", category: "Social Media", excerpt: "", body: "", read_time: "5 min read", cover: "", featured: false };

export const BlogEditor = ({ open, post, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open) setForm(post ? { ...EMPTY, ...post } : EMPTY);
  }, [open, post]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    if (!form.title) {
      toast.error("Add a title first so the image matches the article");
      return;
    }
    setGenerating(true);
    try {
      const res = await adminGenerateCover({ title: form.title, category: form.category, excerpt: form.excerpt });
      set("cover", res.cover);
      toast.success("Cover image generated");
    } catch {
      toast.error("Could not generate an image. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.excerpt || !form.body) {
      toast.error("Title, excerpt and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        excerpt: form.excerpt,
        body: form.body,
        read_time: form.read_time,
        cover: form.cover,
        featured: form.featured,
      };
      if (post) await adminUpdateInsight(post.id, payload);
      else await adminCreateInsight(payload);
      toast.success(post ? "Post updated" : "Post published");
      onSaved();
    } catch (err) {
      console.error("Blog save failed:", err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error("Your session has expired. Please log in again.");
      } else if (status === 413) {
        toast.error("Cover image is too large. Please use a smaller image or paste an image URL instead of uploading.");
      } else if (detail) {
        toast.error(`Save failed: ${typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 120)}`);
      } else if (!err?.response) {
        toast.error("Network error. Check your connection and try again.");
      } else {
        toast.error(`Save failed (${status || "unknown error"}). Please try again.`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{post ? "Edit post" : "New post"}</DialogTitle>
          <DialogDescription>Write and publish an article to your Insights page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Title</label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Article title" data-testid="editor-title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="editor-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Read time</label>
              <Input value={form.read_time} onChange={(e) => set("read_time", e.target.value)} placeholder="6 min read" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Excerpt</label>
            <Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} placeholder="A short summary shown on the Insights list" data-testid="editor-excerpt" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Body</label>
            <Textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={12} className="font-mono text-sm" placeholder={"Write your article.\n\nLeave a BLANK LINE between paragraphs.\n\n## Two hashes for a subheading\n### Three hashes for a smaller subheading\n\n**text between double asterisks** becomes bold\n*text between single asterisks* becomes italic (or _use underscores_)\n\n* Start lines with an asterisk for a bullet\n- Or a hyphen\n• Or a bullet character"} data-testid="editor-body" />
            <p className="text-xs text-[#8a8a8a] mt-1.5 leading-relaxed">
              Formatting: <code>##&nbsp;Heading</code>, <code>###&nbsp;Sub-heading</code>, bullets with <code>*</code> <code>-</code> or <code>•</code>, <code>**bold**</code>, <code>*italic*</code> or <code>_italic_</code>. <strong>Always leave a blank line</strong> between paragraphs, headings and bullet lists.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Cover image</label>
            <div className="flex gap-2">
              <Input value={form.cover} onChange={(e) => set("cover", e.target.value)} placeholder="Image URL, or generate one" data-testid="editor-cover" />
              <Button type="button" variant="outline" onClick={handleGenerate} disabled={generating} data-testid="generate-cover-btn" className="shrink-0">
                {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                <span className="ml-1.5 hidden sm:inline">{generating ? "Generating" : "Generate with AI"}</span>
              </Button>
            </div>
            {form.cover && (
              <div className="mt-3 relative w-full h-40 rounded-xl overflow-hidden bg-[#fafafa] border border-[#eee]">
                <img src={resolveImg(form.cover)} alt="Cover preview" className="w-full h-full object-cover" />
                <button onClick={() => set("cover", "")} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow" aria-label="Remove cover"><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#eee] px-4 py-3">
            <div>
              <p className="text-sm font-medium">Featured</p>
              <p className="text-xs text-[#8a8a8a]">Highlight this post at the top of Insights</p>
            </div>
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="editor-featured" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="editor-save" className="bg-[#7C6CF0] hover:bg-[#5F4FD6]">
            {saving && <Loader2 className="animate-spin mr-2" size={16} />}
            {post ? "Save changes" : "Publish post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogEditor;
